import { Platform } from 'react-native';

const E2E_RUN_STORAGE_KEY = 'paperzilla-e2e-run-id';
const E2E_TAG_STORAGE_KEY = 'paperzilla-e2e-run-tag';
const E2E_RUN_QUERY_KEY = 'e2e_run_id';
const E2E_TAG_QUERY_KEY = 'e2e_run_tag';

export interface E2ETestRunContext {
    runId: string;
    tag: string;
}

function sanitizeToken(value: string | null | undefined, maxLength = 64): string | null {
    if (!value) {
        return null;
    }

    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');

    if (!normalized) {
        return null;
    }

    return normalized.slice(0, maxLength);
}

function readQueryParam(paramName: string): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const currentUrl = new URL(window.location.href);
        return currentUrl.searchParams.get(paramName);
    } catch {
        return null;
    }
}

function resolveWebRunContext(): E2ETestRunContext | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const runIdFromQuery = sanitizeToken(readQueryParam(E2E_RUN_QUERY_KEY), 80);
    const tagFromQuery = sanitizeToken(readQueryParam(E2E_TAG_QUERY_KEY), 40);

    let resolvedRunId = runIdFromQuery;
    if (!resolvedRunId) {
        try {
            resolvedRunId = sanitizeToken(window.sessionStorage.getItem(E2E_RUN_STORAGE_KEY), 80);
        } catch {
            resolvedRunId = null;
        }
    }

    if (!resolvedRunId) {
        resolvedRunId = sanitizeToken(process.env.EXPO_PUBLIC_E2E_RUN_ID ?? null, 80);
    }

    if (!resolvedRunId) {
        return null;
    }

    let resolvedTag = tagFromQuery;
    if (!resolvedTag) {
        try {
            resolvedTag = sanitizeToken(window.sessionStorage.getItem(E2E_TAG_STORAGE_KEY), 40);
        } catch {
            resolvedTag = null;
        }
    }

    if (!resolvedTag) {
        resolvedTag = sanitizeToken(process.env.EXPO_PUBLIC_E2E_RUN_TAG ?? null, 40);
    }

    if (!resolvedTag) {
        resolvedTag = `e2e-${resolvedRunId.slice(0, 8)}`;
    }

    try {
        window.sessionStorage.setItem(E2E_RUN_STORAGE_KEY, resolvedRunId);
        window.sessionStorage.setItem(E2E_TAG_STORAGE_KEY, resolvedTag);
    } catch {
        // Ignore storage issues in private mode or restricted browsers.
    }

    return {
        runId: resolvedRunId,
        tag: resolvedTag,
    };
}

function resolveNativeRunContext(): E2ETestRunContext | null {
    const runId = sanitizeToken(process.env.EXPO_PUBLIC_E2E_RUN_ID ?? null, 80);
    if (!runId) {
        return null;
    }

    const tag = sanitizeToken(process.env.EXPO_PUBLIC_E2E_RUN_TAG ?? null, 40)
        || `e2e-${runId.slice(0, 8)}`;

    return { runId, tag };
}

export function getE2ETestRunContext(): E2ETestRunContext | null {
    return Platform.OS === 'web' ? resolveWebRunContext() : resolveNativeRunContext();
}

export function withE2ETestTag(projectName: string): string {
    const context = getE2ETestRunContext();
    if (!context) {
        return projectName;
    }

    return `[${context.tag}] ${projectName}`;
}

